import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import { useToast } from "../context/ToastContext";

type PaymentCondition = {
  id: string | number;
  code: string;
  name: string;
  provider: string | null;
  payment_method: string;
  installments: number;
  card_brand: string | null;
  bank_name: string | null;
  promotion_name: string | null;
  fee_pct: number | string;
  fee_vat_pct: number | string;
  additional_pct: number | string;
  fixed_fee: number | string;
  valid_from: string;
  valid_to: string | null;
  active: boolean;
};

const METHOD_LABELS: Record<string, string> = {
  TRANSFER: "Transferencia",
  CASH: "Efectivo",
  CARD_1_PAYMENT: "Tarjeta · 1 pago",
  CARD_UP_TO_6: "Tarjeta · cuotas",
};

function pct(value: number | string) {
  return `${(Number(value || 0) * 100).toFixed(2)}%`;
}

function money(value: number | string) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function PaymentConditionsConfig() {
  const { show } = useToast();

  const [items, setItems] = useState<PaymentCondition[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("TRANSFER");
  const [installments, setInstallments] = useState("1");
  const [cardBrand, setCardBrand] = useState("");
  const [bankName, setBankName] = useState("");
  const [promotionName, setPromotionName] = useState("");
  const [feePct, setFeePct] = useState("0");
  const [feeVatPct, setFeeVatPct] = useState("21");
  const [additionalPct, setAdditionalPct] = useState("0");
  const [fixedFee, setFixedFee] = useState("0");
  const [validFrom, setValidFrom] = useState(today());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.getPaymentConditions());
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar las condiciones de cobro.", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    setCode("");
    setName("");
    setProvider("");
    setPaymentMethod("TRANSFER");
    setInstallments("1");
    setCardBrand("");
    setBankName("");
    setPromotionName("");
    setFeePct("0");
    setFeeVatPct("21");
    setAdditionalPct("0");
    setFixedFee("0");
    setValidFrom(today());
    setShowForm(true);
  }

  function closeForm() {
    if (!submitting) setShowForm(false);
  }

  async function submit(e: any) {
    e.preventDefault();

    if (!code.trim() || !name.trim() || !validFrom) {
      show("Completá código, nombre y vigencia.", "error");
      return;
    }

    const installmentNumber = Number(installments);

    if (!Number.isInteger(installmentNumber) || installmentNumber < 1) {
      show("La cantidad de cuotas debe ser al menos 1.", "error");
      return;
    }

    setSubmitting(true);

    try {
      await api.createPaymentCondition({
        code: code.trim().toLowerCase(),
        name: name.trim(),
        provider: provider.trim() || null,
        payment_method: paymentMethod as any,
        installments: installmentNumber,
        card_brand: cardBrand.trim() || null,
        bank_name: bankName.trim() || null,
        promotion_name: promotionName.trim() || null,
        fee_pct: Number(feePct || 0) / 100,
        fee_vat_pct: Number(feeVatPct || 0) / 100,
        additional_pct: Number(additionalPct || 0) / 100,
        fixed_fee: Number(fixedFee || 0),
        valid_from: validFrom,
        active: true,
      });

      show("Condición de cobro creada.", "success");
      setShowForm(false);
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo guardar la condición de cobro.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function deactivate(item: PaymentCondition) {
    if (!item.active) return;

    try {
      await api.updatePaymentCondition(item.id, {
        active: false,
        valid_to: today(),
      });

      show("Condición cerrada. El historial quedó preservado.", "success");
      await load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo cerrar la condición.", "error");
    }
  }

  return (
    <>
      <div className="kanban-header" style={{ marginTop: 36 }}>
        <div>
          <h2>Condiciones de cobro</h2>
          <p className="meta">
            Costos financieros reales por proveedor, medio de pago, cuotas y vigencia.
          </p>
        </div>

        <button className="btn-primary" onClick={openNew}>
          + Nueva condición
        </button>
      </div>

      {loading && <div className="loading-strip">Cargando...</div>}

      <div className="contact-list">
        {items.map((item) => (
          <div key={item.id} className="contact-row">
            <div style={{ flex: 1 }}>
              <div className="name">
                {item.name}
                {!item.active && " · Histórica"}
              </div>

              <div className="meta">
                {item.provider || "Sin proveedor"}
                {" · "}
                {METHOD_LABELS[item.payment_method] || item.payment_method}
                {item.payment_method === "CARD_UP_TO_6"
                  ? ` · ${item.installments} cuotas`
                  : ""}
              </div>

              <div className="meta">
                Comisión: {pct(item.fee_pct)}
                {" · IVA comisión: "}
                {pct(item.fee_vat_pct)}
                {" · Adicional: "}
                {pct(item.additional_pct)}
                {" · Fijo: "}
                {money(item.fixed_fee)}
              </div>

              <div className="meta">
                Vigente desde {String(item.valid_from).slice(0, 10)}
                {item.valid_to
                  ? ` hasta ${String(item.valid_to).slice(0, 10)}`
                  : " · sin fecha de cierre"}
              </div>

              {(item.card_brand || item.bank_name || item.promotion_name) && (
                <div className="meta">
                  {item.card_brand ? `Tarjeta: ${item.card_brand}` : ""}
                  {item.bank_name ? ` · Banco: ${item.bank_name}` : ""}
                  {item.promotion_name ? ` · Promo: ${item.promotion_name}` : ""}
                </div>
              )}
            </div>

            {item.active && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => deactivate(item)}
              >
                Cerrar vigencia
              </button>
            )}
          </div>
        ))}

        {!loading && items.length === 0 && (
          <p className="meta">
            Todavía no hay condiciones de cobro configuradas.
          </p>
        )}
      </div>

      {showForm && (
        <div className="modal-backdrop" onClick={closeForm}>
          <div
            className="modal modal-wide"
            onClick={(e: any) => e.stopPropagation()}
          >
            <h3>Nueva condición de cobro</h3>

            <p className="meta">
              Cada cambio futuro de comisión debe crearse como una nueva versión.
              Así las cotizaciones y ventas históricas conservan sus valores originales.
            </p>

            <form onSubmit={submit} className="form-grid">
              <label>Nombre</label>
              <input
                value={name}
                onChange={(e: any) => setName(e.target.value)}
                placeholder="Ej: Mercado Pago · 6 cuotas"
                required
              />

              <label>Código</label>
              <input
                value={code}
                onChange={(e: any) => setCode(e.target.value)}
                placeholder="Ej: mp_6_cuotas"
                required
              />

              <label>Proveedor / adquirente</label>
              <input
                value={provider}
                onChange={(e: any) => setProvider(e.target.value)}
                placeholder="Ej: Mercado Pago, Getnet, Payway"
              />

              <label>Medio de pago</label>
              <select
                value={paymentMethod}
                onChange={(e: any) => {
                  const value = e.target.value;
                  setPaymentMethod(value);
                  if (value !== "CARD_UP_TO_6") setInstallments("1");
                }}
              >
                <option value="TRANSFER">Transferencia</option>
                <option value="CASH">Efectivo</option>
                <option value="CARD_1_PAYMENT">Tarjeta · 1 pago</option>
                <option value="CARD_UP_TO_6">Tarjeta · cuotas</option>
              </select>

              <label>Cuotas</label>
              <input
                type="number"
                min="1"
                value={installments}
                onChange={(e: any) => setInstallments(e.target.value)}
                disabled={paymentMethod !== "CARD_UP_TO_6"}
              />

              <label>Marca de tarjeta</label>
              <input
                value={cardBrand}
                onChange={(e: any) => setCardBrand(e.target.value)}
                placeholder="Opcional · Visa, Mastercard..."
              />

              <label>Banco</label>
              <input
                value={bankName}
                onChange={(e: any) => setBankName(e.target.value)}
                placeholder="Opcional"
              />

              <label>Promoción</label>
              <input
                value={promotionName}
                onChange={(e: any) => setPromotionName(e.target.value)}
                placeholder="Opcional"
              />

              <label>Comisión del procesador (%)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={feePct}
                onChange={(e: any) => setFeePct(e.target.value)}
              />

              <label>IVA sobre comisión (%)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={feeVatPct}
                onChange={(e: any) => setFeeVatPct(e.target.value)}
              />

              <label>Costo adicional (%)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={additionalPct}
                onChange={(e: any) => setAdditionalPct(e.target.value)}
              />

              <label>Cargo fijo ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fixedFee}
                onChange={(e: any) => setFixedFee(e.target.value)}
              />

              <label>Vigente desde</label>
              <input
                type="date"
                value={validFrom}
                onChange={(e: any) => setValidFrom(e.target.value)}
                required
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={closeForm}
                  disabled={submitting}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Guardando..." : "Crear condición"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
